import React, { useState, useEffect } from 'react';
import { getStatusChipSx } from '../../../constants/statusColors';
import {
  Paper,
  Typography,
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  Chip,
  Tabs,
  Tab,
  Divider,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  AddOutlined as AddIcon,
  Domain as SubsidiaryIcon,
  ChevronRightOutlined as ChevronRightIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useGlobalPermission } from '../../../hooks/useGlobalPermission';
import CompanyHierarchyChart, { buildHierarchyData } from '../../../components/charts/CompanyHierarchyChart';
import { csAdminApi } from '../../../services/api';
import { mapCompanyFromApi } from '../../../utils/apiMappers';
import { formatShareholdingPercent } from '../../../utils/shareholdingPercent';

const mapSubsidiaryRow = (row) => {
  const mapped = mapCompanyFromApi(row);
  if (!mapped) return null;
  return {
    id: mapped.id,
    name: mapped.name,
    tradeLicenseNumber: mapped.tradeLicenseNumber || '—',
    type: mapped.type || 'SUBSIDIARY',
    shareholding: mapped.shareholding_percentage ?? mapped.shareholdingPercentage ?? '—',
    status: mapped.status || 'ACTIVE',
    country: mapped.country || 'UAE',
    directors: mapped.director_count ?? 0,
    committees: 0,
  };
};

const SubsidiariesTab = ({ companyId, company }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { allowed: canCreateCompanies, loading: permissionLoading } = useGlobalPermission('companies', 'create');
  const showCreateSubsidiary = canCreateCompanies && !permissionLoading;
  const [subsidiaries, setSubsidiaries] = useState([]);
  const [viewMode, setViewMode] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSubsidiaries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, company?.subsidiaries]);

  const fetchSubsidiaries = async () => {
    setLoading(true);
    try {
      if (Array.isArray(company?.subsidiaries) && company.subsidiaries.length) {
        setSubsidiaries(company.subsidiaries.map(mapSubsidiaryRow).filter(Boolean));
        return;
      }
      const res = await csAdminApi.getCompanies();
      const rows = (res.data?.data || []).filter(
        (row) => String(row.parent_company_id ?? row.parentCompanyId) === String(companyId)
      );
      setSubsidiaries(rows.map(mapSubsidiaryRow).filter(Boolean));
    } catch (err) {
      console.error('Failed to load subsidiaries:', err);
      setSubsidiaries([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyClick = (id) => {
    navigate(`/companies/${id}`);
  };

  const currentCompany = company || {
    id: companyId,
    name: '—',
    tradeLicenseNumber: '—',
    type: 'HOLDING',
    status: 'ACTIVE',
  };

  const chartData = buildHierarchyData(
    currentCompany,
    subsidiaries,
    [],
    handleCompanyClick
  );

  if (loading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Subsidiaries</Typography>
        {showCreateSubsidiary && (
          <Button variant="contained" startIcon={<AddIcon />} size="small" onClick={() => navigate('/companies/create')}>
            Add Subsidiary
          </Button>
        )}
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={viewMode}
          onChange={(e, newValue) => setViewMode(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Category View" />
          <Tab label="Org Chart" />
        </Tabs>
      </Box>

      {viewMode === 0 && (
        <>
          {subsidiaries.length === 0 ? (
            <Typography variant="body2" color="textSecondary" sx={{ py: 2, textAlign: 'center' }}>
              No subsidiaries found
            </Typography>
          ) : (
            <List sx={{ p: 0 }}>
              {subsidiaries.map((subsidiary, index) => (
                <React.Fragment key={subsidiary.id}>
                  <ListItem
                    sx={{
                      px: 0,
                      py: 2,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.02)' },
                    }}
                    onClick={() => handleCompanyClick(subsidiary.id)}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '8px',
                        bgcolor: subsidiary.type === 'Associate' ? '#fee2e2' : '#fecaca',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        mr: 2,
                      }}
                    >
                      <SubsidiaryIcon sx={{ fontSize: 28, color: subsidiary.type === 'Associate' ? '#f87171' : '#b91c1c' }} />
                    </Box>
                    <ListItemText
                      primary={(
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {subsidiary.name}
                          </Typography>
                          <Chip
                            label={subsidiary.status}
                            sx={{
                              ...getStatusChipSx(subsidiary.status),
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              height: 20,
                              borderRadius: '10px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                            }}
                            size="small"
                          />
                          {subsidiary.shareholding !== '—' && (
                            <Chip
                              label={`${formatShareholdingPercent(subsidiary.shareholding, { suffix: false })}% Shareholding`}
                              sx={{
                                bgcolor: '#F5F5F5',
                                color: '#616161',
                                fontWeight: 500,
                                fontSize: '0.7rem',
                                height: 20,
                                borderRadius: '10px',
                              }}
                              size="small"
                            />
                          )}
                        </Box>
                      )}
                      secondary={(
                        <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                          <Typography variant="body2" color="textSecondary">
                            {subsidiary.tradeLicenseNumber}
                          </Typography>
                          {subsidiary.country && subsidiary.country !== 'UAE' && (
                            <Typography variant="body2" color="textSecondary">
                              • {subsidiary.country}
                            </Typography>
                          )}
                          <Typography variant="body2" color="textSecondary">
                            • {subsidiary.directors} Directors
                          </Typography>
                        </Box>
                      )}
                    />
                    <IconButton size="small">
                      <ChevronRightIcon />
                    </IconButton>
                  </ListItem>
                  {index < subsidiaries.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </>
      )}

      {viewMode === 1 && subsidiaries.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <CompanyHierarchyChart
            key={`subsidiaries-chart-${subsidiaries.length}`}
            nodes={chartData.nodes}
            edges={chartData.edges}
            onNodeClick={handleCompanyClick}
            height={500}
            showControls
          />
        </Box>
      )}
    </Paper>
  );
};

export default SubsidiariesTab;
